// Account-list filters + search + chip strip — FE-038.
//
// The chip row reflects whatever drill-through context the user
// arrived with: dashboard URLs from FE-031..FE-037 already encode
// risk_level / has_overdue / going_cold / renewal_*. Each chip has
// an X-button that clears that single key and updates the URL.

import { Search, X } from "lucide-react";
import { useEffect, useState } from "react";

import type { AccountListFilters as Filters } from "./types";


interface AccountListFiltersProps {
  filters: Filters;
  onChange: (next: Filters) => void;
  onClear: () => void;
}


export function AccountListFilters({
  filters, onChange, onClear,
}: AccountListFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search ?? "");

  // Keep the controlled search input in sync when the URL drives
  // a filter change (back/forward, "Clear all", etc.).
  useEffect(() => {
    setSearchInput(filters.search ?? "");
  }, [filters.search]);

  // Debounce typing → apply: pressing Enter still works (form
  // submit fires immediately via ``onSubmit`` below); typing alone
  // applies after a quiet window so users don't have to hit Enter.
  // 250ms matches the FilterPopover / DependencyPickerModal debounce
  // already in use elsewhere in the FE.
  useEffect(() => {
    const trimmed = searchInput.trim();
    const current = filters.search ?? "";
    if (trimmed === current) return;
    const t = window.setTimeout(() => {
      onChange({ ...filters, search: trimmed || undefined });
    }, 250);
    return () => window.clearTimeout(t);
    // ``onChange`` is recreated each render by AccountListPage so we
    // intentionally exclude it from the dep list; the debounce reacts
    // only to typing and to URL-driven ``filters.search`` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, filters.search]);

  const chips = buildChips(filters);
  const hasAny = chips.length > 0;

  return (
    <div data-testid="account-list-filters" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={(value) => onChange({ ...filters, search: value || undefined })}
        />
        <StatusToggle
          value={filters.status ?? "active"}
          onChange={(status) => onChange({ ...filters, status })}
        />
        {hasAny && (
          <button
            type="button"
            data-testid="account-list-clear-filters"
            onClick={onClear}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-secondary transition-fast ease-out hover:text-text-primary"
          >
            Clear filters
          </button>
        )}
      </div>
      {hasAny && (
        <ul
          data-testid="account-list-chips"
          role="list"
          className="flex flex-wrap items-center gap-2"
        >
          {chips.map((chip) => (
            <li key={chip.key}>
              <FilterChip
                chipKey={chip.key}
                label={chip.label}
                onRemove={() => onChange(removeChip(filters, chip.key))}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


type ChipKey =
  | "risk_level" | "has_overdue" | "renewal_month"
  | "renewal_status" | "renewal_attention" | "going_cold" | "id";


interface ChipDescriptor {
  key: ChipKey;
  label: string;
}


function buildChips(filters: Filters): ChipDescriptor[] {
  const chips: ChipDescriptor[] = [];
  if (filters.risk_level) {
    chips.push({
      key: "risk_level",
      label: `Risk: ${capitalise(filters.risk_level)}`,
    });
  }
  if (filters.has_overdue === true) {
    chips.push({ key: "has_overdue", label: "Has overdue" });
  }
  if (filters.has_overdue === false) {
    chips.push({ key: "has_overdue", label: "No overdue" });
  }
  if (filters.renewal_month) {
    chips.push({
      key: "renewal_month",
      label: `Renewal: ${formatRenewalMonth(filters.renewal_month)}`,
    });
  }
  if (filters.renewal_status) {
    chips.push({
      key: "renewal_status",
      label: `Renewal status: ${formatRenewalStatus(filters.renewal_status)}`,
    });
  }
  if (filters.renewal_attention) {
    chips.push({
      key: "renewal_attention",
      label: "Needs renewal attention",
    });
  }
  if (filters.going_cold) {
    chips.push({ key: "going_cold", label: "Going cold" });
  }
  if (filters.id) {
    chips.push({ key: "id", label: "Pinned to one Account" });
  }
  return chips;
}


function removeChip(filters: Filters, key: ChipKey): Filters {
  const next: Filters = { ...filters };
  delete next[key];
  return next;
}


function FilterChip({
  chipKey, label, onRemove,
}: { chipKey: ChipKey; label: string; onRemove: () => void }) {
  return (
    <span
      data-testid={`account-chip-${chipKey}`}
      className="inline-flex items-center gap-1.5 rounded-full bg-recessed px-2.5 py-1 text-xs text-text-primary"
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        data-testid={`account-chip-remove-${chipKey}`}
        className="text-text-tertiary transition-fast ease-out hover:text-text-primary"
      >
        <X size={12} />
      </button>
    </span>
  );
}


function SearchInput({
  value, onChange, onSubmit,
}: {
  value: string;
  onChange: (next: string) => void;
  onSubmit: (next: string) => void;
}) {
  return (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(value.trim());
      }}
      className="flex-1 sm:max-w-xs"
    >
      <label className="relative flex items-center">
        <span className="sr-only">Search Accounts</span>
        <Search
          size={14}
          className="pointer-events-none absolute left-2.5 text-text-tertiary"
          aria-hidden="true"
        />
        <input
          type="search"
          data-testid="account-list-search-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search Accounts"
          className="w-full rounded-md border border-border-subtle bg-raised py-1.5 pl-7 pr-3 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
        />
      </label>
    </form>
  );
}


function StatusToggle({
  value, onChange,
}: {
  value: NonNullable<Filters["status"]>;
  onChange: (next: NonNullable<Filters["status"]>) => void;
}) {
  const opts: Array<{ value: NonNullable<Filters["status"]>; label: string }> =
    [
      { value: "active", label: "Active" },
      { value: "archived", label: "Archived" },
      { value: "all", label: "All" },
    ];
  return (
    <div
      data-testid="account-list-status-toggle"
      role="group"
      aria-label="Account status"
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised p-1"
    >
      {opts.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            data-testid={`account-list-status-${opt.value}`}
            className={[
              "rounded px-2.5 py-1 text-xs font-medium transition-fast ease-out",
              active
                ? "bg-teal-tint text-text-primary"
                : "text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}


function capitalise(value: string): string {
  return value.length === 0 ? value : value[0].toUpperCase() + value.slice(1);
}


function formatRenewalMonth(value: string): string {
  // ``YYYY-MM`` → ``Aug 2026``.
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return value;
  const year = Number(match[1]);
  const monthIdx = Number(match[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return value;
  const date = new Date(Date.UTC(year, monthIdx, 1));
  return date.toLocaleDateString(undefined, {
    month: "short", year: "numeric", timeZone: "UTC",
  });
}


function formatRenewalStatus(value: string): string {
  switch (value) {
    case "on_track": return "On track";
    case "behind": return "Behind";
    case "at_risk": return "At risk";
    case "not_started": return "Not started";
    default: return value;
  }
}
